import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { formatOrderReference } from '../../../../../utils/formatOrderReference';
const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:3001';

const ORDERS_PAGE_SIZE = 10;

type CustomerDetails = {
  customer: {
    id: string;
    email: string;
    status: string;
    createdAt: string;
    lastLoginAt: string | null;
    preferences?: {
      language: string;
      currency: string;
      fontSizePreset: string;
    } | null;
    customerProfile?: {
      fullName: string | null;
      phone: string | null;
      street: string | null;
      city: string | null;
      postalCode: string | null;
      country: string | null;
      latitude: number | null;
      longitude: number | null;
      drivingDurationSeconds?: number | null;
      drivingDistanceMeters?: number | null;
      flightDurationSeconds?: number | null;
      isAbroadDelivery?: boolean | null;
      abroadRoutePlan?: {
        mode?: string;
        totalDurationSeconds?: number;
        totalDistanceMeters?: number;
        originAirport?: { code?: string; name?: string; country?: string };
        destinationAirport?: { code?: string; name?: string; country?: string };
        legs?: Array<{
          type?: 'DRIVE_TO_AIRPORT' | 'FLIGHT' | 'DRIVE_TO_DESTINATION';
          label?: string;
          durationSeconds?: number;
          distanceMeters?: number;
          from?: string;
          to?: string;
        }>;
      } | null;
      drivingComputedAt?: string | null;
      drivingRouteStatus?: string | null;
      drivingRouteError?: string | null;
    } | null;
  };
  orders: Array<{
    id: string;
    status: string;
    createdAt: string;
    totalUsd: unknown;
    addressSnapshot: { city?: string };
    items: Array<{
      id: string;
      productNameSnapshot: string;
      qty: number;
      unit: unknown;
      lineTotalUsd: unknown;
    }>;
  }>;
  totals: {
    orderCount: number;
    totalSpendUsd: unknown;
    lastOrderAt: string | null;
    orderCountByStatus: Record<string, number>;
    spendByStatus: Record<string, number>;
  };
  inactivity?: {
    lastActivityAt: string | null;
    inactivityDays: number;
    status: 'ACTIVE' | 'WARNED' | 'PENDING_ADMIN_APPROVAL';
    warningCountSent: number;
    blockedAt: string | null;
    pendingAdminApprovalAt: string | null;
    lastWarningSentAt: string | null;
  };
  accountDeletion?: {
    retainedEmail: {
      deleteCount: number;
      firstSeenAt: string;
      lastDeletedAt: string | null;
    } | null;
    archives: Array<{
      id: string;
      deletedAt: string;
      exitSurvey: Record<string, unknown> | null;
    }>;
    recentAttempts: Array<{ id: string; kind: string; createdAt: string }>;
  };
};

function formatInactivityBadge(days?: number | null): string {
  if (days == null || !Number.isFinite(days)) return '—';
  if (days < 30) return `${days} day${days === 1 ? '' : 's'}`;
  const months = Math.floor(days / 30);
  if (months < 24) return `${months} month${months === 1 ? '' : 's'}`;
  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? '' : 's'}`;
}

function formatDriveDurationLabel(
  p: CustomerDetails['customer']['customerProfile'] | null | undefined,
): string {
  if (!p) return '—';
  const sec = p.drivingDurationSeconds;
  if (sec == null) {
    if (p.drivingRouteStatus === 'PENDING') return '…';
    return '—';
  }
  const m = Math.round(sec / 60);
  return `${m} min`;
}

function formatDurationCompact(seconds?: number | null): string {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  const s = Math.max(0, Math.round(seconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const parts: string[] = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  if (m > 0 || parts.length === 0) parts.push(`${m}m`);
  return parts.join(' ');
}

function formatMiles(meters?: number | null): string {
  if (meters == null || !Number.isFinite(meters) || meters <= 0) return '—';
  const miles = meters / 1609.344;
  return `${miles.toFixed(miles >= 10 ? 1 : 2)} mi`;
}

function parseExitSurveyPayload(
  raw: Record<string, unknown> | null,
): { reasonIds: string[]; comment?: string } | null {
  if (!raw) return null;
  const ids = raw.reasonIds;
  if (!Array.isArray(ids)) return null;
  const reasonIds = ids.filter((x): x is string => typeof x === 'string');
  const comment = raw.comment;
  return {
    reasonIds,
    comment: typeof comment === 'string' && comment.trim() ? comment : undefined,
  };
}

function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { authorizedFetch, auth } = useAuth();
  const { t } = useTranslation();
  const listSearch =
    searchParams.get('return') != null
      ? decodeURIComponent(searchParams.get('return') || '')
      : '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [routeSyncNotice, setRouteSyncNotice] = useState<string | null>(null);
  const [routeSyncBusy, setRouteSyncBusy] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [inactivityActionBusy, setInactivityActionBusy] = useState(false);
  const [statusNotice, setStatusNotice] = useState<string | null>(null);
  const [routeBreakdownOpen, setRouteBreakdownOpen] = useState(false);
  const [details, setDetails] = useState<CustomerDetails | null>(null);
  const [ordersPage, setOrdersPage] = useState(1);
  const canRecomputeRoute =
    auth.type === 'ADMIN' ||
    (auth.permissions ?? []).includes('DELIVERY_PRICING:UPDATE');
  const canSuspendCustomer =
    auth.type === 'ADMIN' || (auth.permissions ?? []).includes('CUSTOMER:UPDATE');

  const loadDetails = React.useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await authorizedFetch(
        `${API_BASE}/auth/workspace/customers/${id}/details`,
      );
      if (!res.ok) {
        throw new Error(t('backoffice.customers_detail_error'));
      }
      const data = (await res.json()) as CustomerDetails;
      setDetails(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('backoffice.customers_detail_error'),
      );
      setDetails(null);
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch, id, t]);

  useEffect(() => {
    setOrdersPage(1);
  }, [id]);

  useEffect(() => {
    void loadDetails();
  }, [loadDetails]);

  const recomputeCustomerRoute = async () => {
    if (!id || !canRecomputeRoute || routeSyncBusy) return;
    setRouteSyncBusy(true);
    setRouteSyncNotice(null);
    setError(null);
    try {
      const res = await authorizedFetch(
        `${API_BASE}/delivery-pricing/customers/${id}/recompute`,
        { method: 'POST' },
      );
      const body = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        enqueueError?: string;
        message?: string;
      };
      if (!res.ok || body.ok === false) {
        throw new Error(
          body.enqueueError ||
            body.message ||
            t('backoffice.customers_detail_error'),
        );
      }
      setRouteSyncNotice('Route recompute queued.');
      await loadDetails();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('backoffice.customers_detail_error'),
      );
    } finally {
      setRouteSyncBusy(false);
    }
  };

  const currency = details?.customer.preferences?.currency ?? 'USD';
  const isBlocked = (details?.customer.status ?? '').toUpperCase() === 'BLOCKED';
  const statusBadgeClass =
    isBlocked
      ? 'rounded-full bg-rose-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-rose-800'
      : 'rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-800';

  const setCustomerStatus = async (nextStatus: 'ACTIVE' | 'BLOCKED') => {
    if (!id || !canSuspendCustomer || statusBusy) return;
    setStatusBusy(true);
    setStatusNotice(null);
    setError(null);
    try {
      const res = await authorizedFetch(
        `${API_BASE}/auth/workspace/customers/${id}/status`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: nextStatus }),
        },
      );
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      if (!res.ok) {
        throw new Error(body.message || t('backoffice.customers_detail_error'));
      }
      setStatusNotice(nextStatus === 'BLOCKED' ? 'Customer suspended.' : 'Customer re-enabled.');
      await loadDetails();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('backoffice.customers_detail_error'));
    } finally {
      setStatusBusy(false);
    }
  };

  const reviewInactivityDeletion = async (action: 'approve' | 'reject') => {
    if (!id || inactivityActionBusy) return;
    setInactivityActionBusy(true);
    setError(null);
    setStatusNotice(null);
    try {
      const res = await authorizedFetch(
        `${API_BASE}/auth/workspace/customers/${id}/inactivity/${action}-deletion`,
        { method: 'POST' },
      );
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      if (!res.ok) throw new Error(body.message || t('backoffice.customers_detail_error'));
      setStatusNotice(
        action === 'approve'
          ? 'Inactivity deletion approved.'
          : 'Inactivity deletion request rejected and customer re-enabled.',
      );
      await loadDetails();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('backoffice.customers_detail_error'));
    } finally {
      setInactivityActionBusy(false);
    }
  };

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath="/workspace/customers"
    >
      <main className="workspace-page">
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to={`/workspace/customers${listSearch}`}
            className="text-sm font-semibold text-brand-600 hover:text-brand-700"
          >
            ← {t('backoffice.customers_detail_back')}
          </Link>
        </div>

        {loading ? (
          <p className="text-sm text-slate-600">
            {t('backoffice.customers_detail_loading')}
          </p>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : !details ? (
          <p className="text-sm text-slate-600">
            {t('backoffice.customers_detail_not_found')}
          </p>
        ) : (
          <>
            <section>
              <h1 className="workspace-page-title">
                {details.customer.customerProfile?.fullName || '—'}
              </h1>
              <p className="workspace-page-subtitle text-slate-600">
                {details.customer.email}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {canSuspendCustomer ? (
                  <button
                    type="button"
                    onClick={() => void setCustomerStatus(isBlocked ? 'ACTIVE' : 'BLOCKED')}
                    disabled={statusBusy}
                    className={[
                      'rounded-lg px-3 py-1.5 text-xs font-semibold uppercase tracking-wide shadow-sm disabled:opacity-50',
                      isBlocked
                        ? 'border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                        : 'border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100',
                    ].join(' ')}
                  >
                    {statusBusy ? 'Updating…' : isBlocked ? 'Unsuspend' : 'Suspend'}
                  </button>
                ) : null}
                {canRecomputeRoute ? (
                  <button
                    type="button"
                    onClick={() => void recomputeCustomerRoute()}
                    disabled={routeSyncBusy}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400 disabled:opacity-50"
                  >
                    {routeSyncBusy
                      ? 'Queueing route recompute…'
                      : 'Recompute customer route'}
                  </button>
                ) : null}
                {details.inactivity?.status === 'PENDING_ADMIN_APPROVAL' && canSuspendCustomer ? (
                  <>
                    <button
                      type="button"
                      onClick={() => void reviewInactivityDeletion('approve')}
                      disabled={inactivityActionBusy}
                      className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-rose-800 hover:bg-rose-100 disabled:opacity-50"
                    >
                      {inactivityActionBusy ? 'Processing…' : 'Approve inactivity deletion'}
                    </button>
                    <button
                      type="button"
                      onClick={() => void reviewInactivityDeletion('reject')}
                      disabled={inactivityActionBusy}
                      className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                    >
                      Keep account active
                    </button>
                  </>
                ) : null}
              </div>
              {routeSyncNotice ? (
                <p className="mt-2 text-xs text-emerald-700">{routeSyncNotice}</p>
              ) : null}
              {statusNotice ? (
                <p className="mt-2 text-xs text-emerald-700">{statusNotice}</p>
              ) : null}
            </section>

            <section className="glass-panel max-w-5xl p-6 sm:p-8">
              <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={statusBadgeClass}>{details.customer.status}</span>
                  {details.customer.customerProfile?.country ? (
                    <span className="text-sm text-slate-600">
                      {details.customer.customerProfile.country}
                    </span>
                  ) : null}
                </div>
                <div className="text-left sm:text-right">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {t('backoffice.customers_detail_total_spend')}
                  </div>
                  <div className="mt-1 text-2xl font-extrabold text-slate-900">
                    {currency.toUpperCase()}{' '}
                    {Number(details.totals.totalSpendUsd).toLocaleString(
                      undefined,
                      { maximumFractionDigits: 2 },
                    )}
                  </div>
                  <div className="text-xs text-slate-600">
                    {details.totals.orderCount}{' '}
                    {t('backoffice.customers_detail_orders')}
                  </div>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-sm font-semibold text-slate-900">
                    {t('backoffice.customers_detail_profile')}
                  </div>
                  <div className="mt-3 space-y-2 text-xs text-slate-700">
                    <div>
                      <span className="font-semibold text-slate-900">
                        {t('backoffice.customers_detail_id')}:
                      </span>{' '}
                      {details.customer.id}
                    </div>
                    <div>
                      <span className="font-semibold text-slate-900">
                        {t('backoffice.customers_table_col_email')}:
                      </span>{' '}
                      {details.customer.email}
                    </div>
                    <div>
                      <span className="font-semibold text-slate-900">
                        {t('backoffice.customers_table_col_phone')}:
                      </span>{' '}
                      {details.customer.customerProfile?.phone || '—'}
                    </div>
                    <div>
                      <span className="font-semibold text-slate-900">
                        {t('backoffice.customers_detail_address')}:
                      </span>{' '}
                      {[
                        details.customer.customerProfile?.street,
                        details.customer.customerProfile?.postalCode,
                        details.customer.customerProfile?.city,
                        details.customer.customerProfile?.country,
                      ]
                        .filter(Boolean)
                        .join(', ') || '—'}
                    </div>
                    <div>
                      <span className="font-semibold text-slate-900">
                        {t('backoffice.customers_detail_lat_lng')}:
                      </span>{' '}
                      {details.customer.customerProfile?.latitude != null &&
                      details.customer.customerProfile?.longitude != null
                        ? `${details.customer.customerProfile.latitude}, ${details.customer.customerProfile.longitude}`
                        : '—'}
                    </div>
                    {!details.customer.customerProfile?.isAbroadDelivery ? (
                      <div>
                        <span className="font-semibold text-slate-900">
                          {t('backoffice.customers_detail_drive_time')}:
                        </span>{' '}
                        {formatDriveDurationLabel(details.customer.customerProfile)}
                      </div>
                    ) : null}
                    {!details.customer.customerProfile?.isAbroadDelivery ? (
                      <div>
                        <span className="font-semibold text-slate-900">
                          {t('backoffice.customers_detail_drive_status')}:
                        </span>{' '}
                        {details.customer.customerProfile?.drivingRouteStatus ?? '—'}
                        {details.customer.customerProfile?.drivingRouteError
                          ? ` (${details.customer.customerProfile.drivingRouteError})`
                          : ''}
                      </div>
                    ) : null}
                    {details.customer.customerProfile?.isAbroadDelivery &&
                    details.customer.customerProfile?.abroadRoutePlan ? (
                      <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3">
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-indigo-900">
                          International route summary
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-indigo-200 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-indigo-900">
                            Mode {(details.customer.customerProfile.abroadRoutePlan.mode || 'INTERMODAL_AIR').split('_').join(' ')}
                          </span>
                          <span className="rounded-full bg-indigo-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-indigo-900">
                            Flight ETA {formatDurationCompact(details.customer.customerProfile.flightDurationSeconds)}
                          </span>
                          <span className="rounded-full bg-indigo-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-indigo-900">
                            Total ETA {formatDurationCompact(details.customer.customerProfile.abroadRoutePlan.totalDurationSeconds)}
                          </span>
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-800">
                            Total {formatMiles(details.customer.customerProfile.abroadRoutePlan.totalDistanceMeters)}
                          </span>
                        </div>
                        <div className="mt-2 text-[11px] text-slate-700">
                          <span className="font-semibold text-slate-900">Air corridor:</span>{' '}
                          {[
                            details.customer.customerProfile.abroadRoutePlan.originAirport?.code ||
                              details.customer.customerProfile.abroadRoutePlan.originAirport?.name,
                            details.customer.customerProfile.abroadRoutePlan.destinationAirport?.code ||
                              details.customer.customerProfile.abroadRoutePlan.destinationAirport?.name,
                          ]
                            .filter(Boolean)
                            .join(' -> ') || '—'}
                        </div>
                        <div className="mt-1 text-[11px] text-slate-600">
                          <span className="font-semibold text-slate-900">Last computed:</span>{' '}
                          {details.customer.customerProfile.drivingComputedAt
                            ? new Date(details.customer.customerProfile.drivingComputedAt).toLocaleString()
                            : '—'}
                        </div>
                      </div>
                    ) : null}
                    {details.customer.customerProfile?.isAbroadDelivery &&
                    details.customer.customerProfile?.abroadRoutePlan ? (
                      <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50/40 p-3">
                        <button
                          type="button"
                          onClick={() => setRouteBreakdownOpen((v) => !v)}
                          className="mb-2 flex w-full items-center justify-between rounded-md bg-indigo-100 px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-wide text-indigo-900"
                        >
                          <span>International route breakdown</span>
                          <ChevronDown
                            className={[
                              'h-4 w-4 transition-transform',
                              routeBreakdownOpen ? 'rotate-180' : 'rotate-0',
                            ].join(' ')}
                          />
                        </button>
                        {routeBreakdownOpen ? (
                          <div className="space-y-2 text-[11px] text-slate-700">
                            <div>
                              <span className="font-semibold text-slate-900">Origin airport:</span>{' '}
                              {[
                                details.customer.customerProfile.abroadRoutePlan.originAirport?.code,
                                details.customer.customerProfile.abroadRoutePlan.originAirport?.name,
                              ]
                                .filter(Boolean)
                                .join(' — ') || '—'}
                            </div>
                            <div>
                              <span className="font-semibold text-slate-900">Destination airport:</span>{' '}
                              {[
                                details.customer.customerProfile.abroadRoutePlan.destinationAirport?.code,
                                details.customer.customerProfile.abroadRoutePlan.destinationAirport?.name,
                              ]
                                .filter(Boolean)
                                .join(' — ') || '—'}
                            </div>
                            {(details.customer.customerProfile.abroadRoutePlan.legs ?? []).map((leg, idx) => (
                              <div key={`${leg.type ?? 'LEG'}-${idx}`} className="rounded-md border border-slate-200 bg-white p-2">
                                <div className="font-semibold text-slate-900">
                                  {idx + 1}. {leg.type === 'DRIVE_TO_AIRPORT'
                                    ? 'Store address -> Origin airport'
                                    : leg.type === 'FLIGHT'
                                      ? 'Origin airport -> Destination airport'
                                      : leg.type === 'DRIVE_TO_DESTINATION'
                                        ? 'Destination airport -> Customer address'
                                        : leg.label || 'Route leg'}
                                </div>
                                <div className="mt-1 text-slate-700">{leg.from || '—'}{' -> '}{leg.to || '—'}</div>
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                  <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-900">
                                    ETA {formatDurationCompact(leg.durationSeconds)}
                                  </span>
                                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                                    {formatMiles(leg.distanceMeters)}
                                  </span>
                                </div>
                              </div>
                            ))}
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              <span className="rounded-full bg-indigo-200 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-indigo-900">
                                Total ETA {formatDurationCompact(details.customer.customerProfile.abroadRoutePlan.totalDurationSeconds)}
                              </span>
                              <span className="rounded-full bg-slate-200 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-800">
                                Total distance {formatMiles(details.customer.customerProfile.abroadRoutePlan.totalDistanceMeters)}
                              </span>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    <div>
                      <span className="font-semibold text-slate-900">
                        {t('backoffice.customers_detail_created')}:
                      </span>{' '}
                      {details.customer.createdAt
                        ? new Date(details.customer.createdAt).toLocaleString()
                        : '—'}
                    </div>
                    <div>
                      <span className="font-semibold text-slate-900">
                        {t('backoffice.customers_detail_last_login')}:
                      </span>{' '}
                      {details.customer.lastLoginAt
                        ? new Date(
                            details.customer.lastLoginAt,
                          ).toLocaleString()
                        : '—'}
                    </div>
                    <div className="rounded-lg border border-sky-200 bg-sky-50/70 p-2">
                      <div>
                        <span className="font-semibold text-slate-900">Last activity:</span>{' '}
                        {details.inactivity?.lastActivityAt
                          ? `${new Date(details.inactivity.lastActivityAt).toLocaleString(undefined, { timeZone: 'UTC' })} (UTC)`
                          : '—'}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-sky-200 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-sky-900">
                          {formatInactivityBadge(details.inactivity?.inactivityDays ?? null)}
                        </span>
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                          {(details.inactivity?.status ?? 'ACTIVE').replace(/_/g, ' ')}
                        </span>
                        {typeof details.inactivity?.warningCountSent === 'number' ? (
                          <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                            warnings {details.inactivity.warningCountSent}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div>
                      <span className="font-semibold text-slate-900">
                        {t('backoffice.customers_detail_preferences')}:
                      </span>{' '}
                      {details.customer.preferences
                        ? `${details.customer.preferences.language} / ${details.customer.preferences.currency}`
                        : '—'}
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-sm font-semibold text-slate-900">
                    {t('backoffice.customers_detail_order_summary')}
                  </div>
                  <div className="mt-3 space-y-2 text-xs text-slate-700">
                    <div>
                      <span className="font-semibold text-slate-900">
                        {t('backoffice.customers_detail_last_order')}:
                      </span>{' '}
                      {details.totals.lastOrderAt
                        ? new Date(
                            details.totals.lastOrderAt,
                          ).toLocaleString()
                        : '—'}
                    </div>
                    <div>
                      <span className="font-semibold text-slate-900">
                        {t('backoffice.customers_detail_status_counts')}:
                      </span>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {Object.keys(details.totals.orderCountByStatus)
                          .length ? (
                          Object.entries(details.totals.orderCountByStatus)
                            .sort(
                              (a, b) =>
                                (b[1] as number) - (a[1] as number),
                            )
                            .map(([status, count]) => (
                              <span
                                key={status}
                                className="rounded-full bg-slate-900/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700"
                              >
                                {status}: {count}
                              </span>
                            ))
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {details.accountDeletion &&
              (details.accountDeletion.retainedEmail ||
                details.accountDeletion.archives.length > 0 ||
                details.accountDeletion.recentAttempts.length > 0) ? (
                <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50/50 p-4 sm:p-5">
                  <div className="text-sm font-semibold text-slate-900">
                    {t('backoffice.customers_detail_account_lifecycle')}
                  </div>
                  <p className="mt-1 text-xs text-slate-600">
                    {t('backoffice.customers_detail_account_lifecycle_hint')}
                  </p>

                  {details.accountDeletion.retainedEmail ? (
                    <div className="mt-4 rounded-lg border border-amber-200 bg-white p-3 text-xs text-slate-800">
                      <div className="font-semibold text-slate-900">
                        {t('backoffice.customers_detail_retained_email_title')}
                      </div>
                      <div className="mt-2 space-y-1">
                        <div>
                          <span className="font-semibold text-slate-900">
                            {t('backoffice.customers_detail_delete_count')}:
                          </span>{' '}
                          {details.accountDeletion.retainedEmail.deleteCount}
                        </div>
                        <div>
                          <span className="font-semibold text-slate-900">
                            {t('backoffice.customers_detail_first_seen')}:
                          </span>{' '}
                          {details.accountDeletion.retainedEmail.firstSeenAt
                            ? new Date(
                                details.accountDeletion.retainedEmail.firstSeenAt,
                              ).toLocaleString()
                            : '—'}
                        </div>
                        <div>
                          <span className="font-semibold text-slate-900">
                            {t('backoffice.customers_detail_last_deleted')}:
                          </span>{' '}
                          {details.accountDeletion.retainedEmail.lastDeletedAt
                            ? new Date(
                                details.accountDeletion.retainedEmail.lastDeletedAt,
                              ).toLocaleString()
                            : '—'}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {details.accountDeletion.archives.length > 0 ? (
                    <div className="mt-4">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                        {t('backoffice.customers_detail_archives')}
                      </div>
                      <ul className="mt-2 space-y-3">
                        {details.accountDeletion.archives.map((a) => {
                          const es = parseExitSurveyPayload(a.exitSurvey);
                          return (
                            <li
                              key={a.id}
                              className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-800"
                            >
                              <div>
                                <span className="font-semibold text-slate-900">
                                  {t('backoffice.customers_detail_archive_deleted_at')}:
                                </span>{' '}
                                {new Date(a.deletedAt).toLocaleString()}
                              </div>
                              {es && (es.reasonIds.length > 0 || es.comment) ? (
                                <div className="mt-2 border-t border-slate-100 pt-2">
                                  <div className="font-semibold text-slate-900">
                                    {t('backoffice.customers_detail_exit_survey')}
                                  </div>
                                  {es.reasonIds.length > 0 ? (
                                    <div className="mt-1">
                                      <span className="font-semibold text-slate-900">
                                        {t('backoffice.customers_detail_exit_reasons')}:
                                      </span>{' '}
                                      {es.reasonIds.join(', ')}
                                    </div>
                                  ) : null}
                                  {es.comment ? (
                                    <div className="mt-1">
                                      <span className="font-semibold text-slate-900">
                                        {t('backoffice.customers_detail_exit_comment')}:
                                      </span>{' '}
                                      {es.comment}
                                    </div>
                                  ) : null}
                                </div>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : null}

                  {details.accountDeletion.recentAttempts.length > 0 ? (
                    <div className="mt-4">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                        {t('backoffice.customers_detail_recent_attempts')}
                      </div>
                      <ul className="mt-2 max-h-48 overflow-auto rounded-lg border border-slate-200 bg-white">
                        {details.accountDeletion.recentAttempts.map((row) => (
                          <li
                            key={row.id}
                            className="flex flex-wrap justify-between gap-2 border-b border-slate-100 px-3 py-2 text-[11px] text-slate-800 last:border-b-0"
                          >
                            <span className="font-mono font-semibold text-slate-900">{row.kind}</span>
                            <span className="text-slate-600">
                              {new Date(row.createdAt).toLocaleString()}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
                <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      {t('backoffice.customers_detail_orders_section')}
                    </div>
                    <div className="text-xs text-slate-600">
                      {t('backoffice.customers_page_showing', {
                        page: ordersPage,
                        pages: Math.max(
                          1,
                          Math.ceil(
                            details.orders.length / ORDERS_PAGE_SIZE,
                          ),
                        ),
                      })}
                    </div>
                  </div>
                  {details.orders.length > ORDERS_PAGE_SIZE ? (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setOrdersPage((p) => Math.max(1, p - 1))
                        }
                        className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400 disabled:opacity-50"
                        disabled={ordersPage <= 1}
                      >
                        {t('backoffice.customers_page_prev')}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setOrdersPage((p) =>
                            Math.min(
                              Math.max(
                                1,
                                Math.ceil(
                                  details.orders.length / ORDERS_PAGE_SIZE,
                                ),
                              ),
                              p + 1,
                            ),
                          )
                        }
                        className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400 disabled:opacity-50"
                        disabled={
                          ordersPage >=
                          Math.ceil(
                            details.orders.length / ORDERS_PAGE_SIZE,
                          )
                        }
                      >
                        {t('backoffice.customers_page_next')}
                      </button>
                    </div>
                  ) : null}
                </div>

                {details.orders.length === 0 ? (
                  <p className="text-sm text-slate-600">
                    {t('backoffice.customers_detail_no_orders')}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-xs sm:text-sm">
                      <thead className="border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="py-2 pr-3">
                            {t('backoffice.customers_detail_col_id')}
                          </th>
                          <th className="py-2 pr-3">
                            {t('backoffice.customers_detail_col_date')}
                          </th>
                          <th className="py-2 pr-3">
                            {t('backoffice.customers_table_col_status')}
                          </th>
                          <th className="py-2 pr-3">
                            {t('backoffice.customers_detail_col_total')}
                          </th>
                          <th className="py-2 pr-3">
                            {t('backoffice.customers_detail_col_city')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {details.orders
                          .slice(
                            (ordersPage - 1) * ORDERS_PAGE_SIZE,
                            ordersPage * ORDERS_PAGE_SIZE,
                          )
                          .map((o) => (
                            <tr key={o.id}>
                              <td className="py-2 pr-3 font-semibold text-slate-900">
                                <span className="font-mono text-[11px] break-all">{formatOrderReference(o.id)}</span>
                              </td>
                              <td className="py-2 pr-3 text-slate-600">
                                {new Date(o.createdAt).toLocaleDateString()}
                              </td>
                              <td className="py-2 pr-3">
                                <span className="rounded-full bg-slate-900/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                                  {o.status}
                                </span>
                              </td>
                              <td className="py-2 pr-3 font-semibold text-slate-900">
                                {currency.toUpperCase()}{' '}
                                {Number(o.totalUsd).toLocaleString(undefined, {
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                              <td className="py-2 pr-3 text-slate-700">
                                <div>{o.addressSnapshot?.city || '—'}</div>
                                <div className="text-[11px] text-slate-500">
                                  {o.items?.length
                                    ? t(
                                        'backoffice.customers_detail_items_count',
                                        { count: o.items.length },
                                      )
                                    : '—'}
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </main>
    </MainLayout>
  );
}

export default CustomerDetailPage;
